import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, MenuItem, Paper,
  Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { ContentCopy as CopyIcon, DeleteOutline as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import apiService from '../../services/api';
import { Confirm, DomainTabProps, TabBody, errorText } from './shared';

interface DsRecord { uuid: string; keyTag?: number | string; algorithm?: number | string; digest?: string; digestType?: number | string }

const SecurityTab: React.FC<DomainTabProps> = ({ serviceId, info, isAdmin, onChanged }) => {
  const locked = /transferprohibited/i.test(info?.status || '');
  const autoRenew = (info?.auto_renew || '').toLowerCase() === 'on';
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ severity: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [confirmUnlock, setConfirmUnlock] = useState(false);
  const [confirmCode, setConfirmCode] = useState(false);
  const [newCode, setNewCode] = useState<string | null>(null);
  const [ds, setDs] = useState<DsRecord[]>([]);
  const [algorithms, setAlgorithms] = useState<Record<string, string>>({});
  const [dsLoading, setDsLoading] = useState(false);
  const [dsDialog, setDsDialog] = useState(false);
  const [dsDraft, setDsDraft] = useState({ key_tag: '', algorithm: '8', digest_type: '2', digest: '' });
  const [dsError, setDsError] = useState<string | null>(null);
  const [dsDelete, setDsDelete] = useState<DsRecord | null>(null);

  const loadDs = useCallback(async () => {
    setDsLoading(true);
    try {
      const data = await apiService.domainRequest<{ records: DsRecord[]; algorithms: Record<string, string> }>('get', serviceId, '/dnssec');
      setDs(data.records || []);
      setAlgorithms(data.algorithms || {});
    } catch {
      setDs([]);
    } finally {
      setDsLoading(false);
    }
  }, [serviceId]);

  useEffect(() => { loadDs(); }, [loadDs]);

  const run = async (key: string, fn: () => Promise<unknown>, ok: string) => {
    setBusy(key);
    setMsg(null);
    try {
      await fn();
      setMsg({ severity: 'success', text: ok });
      await onChanged();
    } catch (e) {
      setMsg({ severity: 'error', text: errorText(e) });
    } finally {
      setBusy(null);
    }
  };

  const generateCode = async () => {
    setBusy('code');
    try {
      const res = await apiService.domainRequest<{ auth_code: string }>('post', serviceId, '/auth-code', {});
      setConfirmCode(false);
      setNewCode(res.auth_code);
      await onChanged();
    } catch (e) {
      setConfirmCode(false);
      setMsg({ severity: 'error', text: errorText(e) });
    } finally {
      setBusy(null);
    }
  };

  const addDs = async () => {
    setBusy('ds');
    setDsError(null);
    try {
      await apiService.domainRequest('post', serviceId, '/dnssec', {
        key_tag: Number(dsDraft.key_tag), algorithm: Number(dsDraft.algorithm), digest_type: Number(dsDraft.digest_type), digest: dsDraft.digest,
      });
      setDsDialog(false);
      setDsDraft({ key_tag: '', algorithm: '8', digest_type: '2', digest: '' });
      setMsg({ severity: 'success', text: 'DNSSEC record added.' });
      await loadDs();
      await onChanged();
    } catch (e) {
      setDsError(errorText(e, 'Could not add the DNSSEC record.'));
    } finally {
      setBusy(null);
    }
  };

  const removeDs = async () => {
    if (!dsDelete) return;
    setBusy('ds');
    try {
      await apiService.domainRequest('delete', serviceId, `/dnssec/${encodeURIComponent(dsDelete.uuid)}`, undefined, { label: `key tag ${dsDelete.keyTag}` });
      setDsDelete(null);
      setMsg({ severity: 'success', text: 'DNSSEC record removed.' });
      await loadDs();
      await onChanged();
    } catch (e) {
      setDsDelete(null);
      setMsg({ severity: 'error', text: errorText(e) });
    } finally {
      setBusy(null);
    }
  };

  return (
    <TabBody>
      {msg && <Alert severity={msg.severity} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}

      <Paper variant="outlined" sx={{ p: 2.5, mb: 2, borderRadius: 0 }}>
        <Typography variant="subtitle2" gutterBottom>Transfer lock</Typography>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Chip size="small" color={locked ? 'success' : 'warning'} label={locked ? 'Locked' : 'Unlocked'} />
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 220 }}>
            A locked domain can't be transferred to another registrar. {isAdmin ? '' : 'Only admins can unlock a domain.'}
          </Typography>
          {locked ? (
            <Button variant="outlined" size="small" disabled={!isAdmin || busy === 'lock'} onClick={() => setConfirmUnlock(true)}>Unlock for transfer</Button>
          ) : (
            <Button variant="contained" size="small" disabled={busy === 'lock'} onClick={() => run('lock', () => apiService.domainRequest('post', serviceId, '/transfer-lock', { locked: true }), 'Domain locked.')}>
              {busy === 'lock' ? <CircularProgress size={18} /> : 'Lock domain'}
            </Button>
          )}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, mb: 2, borderRadius: 0 }}>
        <Typography variant="subtitle2" gutterBottom>Renewal</Typography>
        <FormControlLabel
          control={
            <Switch
              checked={autoRenew}
              disabled={busy === 'renew'}
              onChange={(e) => run('renew', () => apiService.domainRequest('post', serviceId, '/auto-renew', { enabled: e.target.checked }), `Auto-renew turned ${e.target.checked ? 'on' : 'off'}.`)}
            />
          }
          label="Auto-renew with Synergy before the domain expires"
        />
        <Typography variant="caption" color="text.secondary" display="block">Renewals are charged to the Synergy Wholesale balance.</Typography>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, mb: 2, borderRadius: 0 }}>
        <Typography variant="subtitle2" gutterBottom>Auth code (EPP password)</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          The current code can be viewed on the Registry tab (admins only). Generating a new one replaces the old code straight away.
        </Typography>
        <Button variant="outlined" size="small" disabled={!isAdmin || busy === 'code'} onClick={() => setConfirmCode(true)}>Generate a new auth code</Button>
        {!isAdmin && <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>Admins only</Typography>}
      </Paper>

      {!!info?.icann_status && (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2, borderRadius: 0 }}>
          <Typography variant="subtitle2" gutterBottom>ICANN registrant verification</Typography>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <Chip size="small" label={info.icann_status} color={/verified/i.test(info.icann_status) ? 'success' : 'warning'} />
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>Unverified registrants have their domain suspended after the deadline.</Typography>
            <Button variant="outlined" size="small" disabled={busy === 'icann'} onClick={() => run('icann', () => apiService.domainRequest('post', serviceId, '/resend-icann-verification'), 'Verification email resent.')}>
              Resend verification email
            </Button>
          </Stack>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2">DNSSEC (DS records)</Typography>
          <Button size="small" variant="outlined" startIcon={<AddIcon />} disabled={!isAdmin} onClick={() => { setDsError(null); setDsDialog(true); }}>Add DS record</Button>
        </Box>
        {!isAdmin && <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>Only admins can change DNSSEC records.</Typography>}
        <Table size="small">
          <TableHead><TableRow><TableCell>Key tag</TableCell><TableCell>Algorithm</TableCell><TableCell>Digest type</TableCell><TableCell>Digest</TableCell><TableCell align="right" /></TableRow></TableHead>
          <TableBody>
            {ds.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 2, color: 'text.secondary' }}>{dsLoading ? 'Loading…' : 'No DNSSEC records (or the extension doesn\'t support DNSSEC).'}</TableCell></TableRow>}
            {ds.map((r) => (
              <TableRow key={r.uuid}>
                <TableCell>{r.keyTag}</TableCell>
                <TableCell>{algorithms[String(r.algorithm)] ? `${r.algorithm} (${algorithms[String(r.algorithm)]})` : r.algorithm}</TableCell>
                <TableCell>{r.digestType}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', wordBreak: 'break-all', maxWidth: 360 }}>{r.digest}</TableCell>
                <TableCell align="right">
                  {isAdmin && <Tooltip title="Remove"><IconButton size="small" color="error" onClick={() => setDsDelete(r)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Confirm open={confirmUnlock} busy={busy === 'lock'} danger title="Unlock this domain?" confirmLabel="Unlock"
        text="Unlocking lets the domain be transferred to another registrar with its auth code. Lock it again once the transfer is done or no longer needed."
        onCancel={() => setConfirmUnlock(false)}
        onConfirm={async () => { await run('lock', () => apiService.domainRequest('post', serviceId, '/transfer-lock', { locked: false }), 'Domain unlocked.'); setConfirmUnlock(false); }} />
      <Confirm open={confirmCode} busy={busy === 'code'} danger title="Generate a new auth code?" confirmLabel="Generate"
        text="The current auth code stops working immediately. The new code is shown once, so copy it before closing." onCancel={() => setConfirmCode(false)} onConfirm={generateCode} />
      <Confirm open={!!dsDelete} busy={busy === 'ds'} danger title="Remove this DNSSEC record?" confirmLabel="Remove"
        text="Removing the only DS record while the zone is still signed can make the domain stop resolving." onCancel={() => setDsDelete(null)} onConfirm={removeDs} />

      <Dialog open={!!newCode} onClose={() => setNewCode(null)} maxWidth="xs" fullWidth>
        <DialogTitle>New auth code</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>This is the only time it is shown here.</Alert>
          <TextField fullWidth value={newCode || ''} InputProps={{ readOnly: true, sx: { fontFamily: 'monospace' }, endAdornment: (
            <IconButton onClick={() => newCode && navigator.clipboard.writeText(newCode)}><CopyIcon fontSize="small" /></IconButton>
          ) }} />
        </DialogContent>
        <DialogActions><Button onClick={() => setNewCode(null)}>Done</Button></DialogActions>
      </Dialog>

      <Dialog open={dsDialog} onClose={busy === 'ds' ? undefined : () => setDsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add DNSSEC DS record</DialogTitle>
        <DialogContent dividers>
          {dsError && <Alert severity="error" sx={{ mb: 2 }}>{dsError}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Key tag" type="number" value={dsDraft.key_tag} onChange={(e) => setDsDraft({ ...dsDraft, key_tag: e.target.value })} />
            <TextField select label="Algorithm" value={dsDraft.algorithm} onChange={(e) => setDsDraft({ ...dsDraft, algorithm: e.target.value })}>
              {Object.entries(algorithms).map(([k, v]) => <MenuItem key={k} value={k}>{k} — {v}</MenuItem>)}
              {!algorithms[dsDraft.algorithm] && <MenuItem value={dsDraft.algorithm}>{dsDraft.algorithm}</MenuItem>}
            </TextField>
            <TextField select label="Digest type" value={dsDraft.digest_type} onChange={(e) => setDsDraft({ ...dsDraft, digest_type: e.target.value })}>
              <MenuItem value="1">1 — SHA-1</MenuItem><MenuItem value="2">2 — SHA-256</MenuItem><MenuItem value="4">4 — SHA-384</MenuItem>
            </TextField>
            <TextField label="Digest (hex)" value={dsDraft.digest} onChange={(e) => setDsDraft({ ...dsDraft, digest: e.target.value })} multiline minRows={2} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDsDialog(false)} disabled={busy === 'ds'}>Cancel</Button>
          <Button variant="contained" onClick={addDs} disabled={busy === 'ds' || !dsDraft.key_tag || !dsDraft.digest.trim()}>{busy === 'ds' ? <CircularProgress size={20} /> : 'Add'}</Button>
        </DialogActions>
      </Dialog>
    </TabBody>
  );
};

export default SecurityTab;
